import NotificationPreferences from "@/components/notifications/NotificationPreferences";
import React from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { useAuth } from "@/contexts/AuthContext"
import { changePassword } from "@/api/settings"
import { useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogOverlay,
} from "@/components/ui/dialog"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { PhoneInputField } from "@/components/ui/phone-input"
import {
  Shield,
  LogOut,
  Trash2,
  Smartphone,
  User as UserIcon,
  KeyRound,
  ArrowDownToLine,
  Info,
  MonitorSmartphone,
  HelpCircle,
  Building2,
  ArrowLeftRight,
  Globe,
} from "lucide-react"
import { useLanguage } from "@/contexts/LanguageContext"
import {
  getResidentProfile,
  updateResidentProfile,
  deactivateResidentAccount,
  getStoredActiveMembership,
  getStoredResidentMemberships,
  type ResidentMembership,
} from "@/api/residentAuth"

/** Brand */
const PRIMARY = "#1F3A93"

/* -----------------------------
 * Types
 * ---------------------------- */
type ProfileForm = {
  fullName: string
  email: string
  phone: string
  unit: string
}


type PasswordForm = {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

/* -----------------------------
 * Reusable styles
 * ---------------------------- */
const sectionTitle = "text-[17px] font-semibold tracking-[-0.01em] text-gray-900"
const sectionDesc = "text-sm text-gray-600"

/** Brand (blue) button — white text */
function PrimaryButton(props: React.ComponentProps<typeof Button>) {
  const { className = "", ...rest } = props
  return (
    <Button
      {...rest}
      className={[
        "px-5 h-10 rounded-full text-[15px] font-medium shadow-sm transition active:scale-[0.99]",
        "bg-[var(--brand)] text-white hover:opacity-90",
        "disabled:!bg-gray-200 disabled:!text-gray-700 disabled:cursor-not-allowed disabled:opacity-100",
        className,
      ].join(" ")}
      style={{ "--brand": PRIMARY } as React.CSSProperties}
    />
  )
}

/** Quiet button — forced dark text & icons (no outline variant) */
function QuietButton(props: React.ComponentProps<typeof Button>) {
  const { className = "", ...rest } = props
  return (
    <Button
      {...rest}
      className={[
        "rounded-full h-10 px-4 text-[15px] font-medium",
        "bg-gray-100 ring-1 ring-black/5 border-0 shadow-sm",
        "!text-gray-900 hover:!text-gray-900",
        "hover:bg-gray-200 active:bg-gray-300",
        "disabled:bg-gray-100 disabled:!text-gray-500 disabled:ring-black/10 disabled:opacity-100",
        "[&_*]:!text-gray-900 [&_*]:!stroke-current",
        "whitespace-nowrap",
        className,
      ].join(" ")}
    />
  )
}

/** iOS-style switch row — responsive and non-squishy */

/** Lightweight callout */
function Callout({
  icon = <Info className="w-4 h-4" />,
  children,
}: {
  icon?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="flex items-start gap-2 rounded-xl ring-1 ring-black/5 p-3.5 bg-gradient-to-b from-gray-50 to-white text-sm text-gray-700">
      <div className="mt-0.5 text-gray-600 shrink-0">{icon}</div>
      <div className="leading-relaxed min-w-0 break-words">{children}</div>
    </div>
  )
}

/** Language Selector Component */
function LanguageSelector({ onChange, disabled }: { onChange: (language: "en" | "es") => void; disabled: boolean }) {
  const { language, t } = useLanguage()

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange("en")}
          className={[
            "flex items-center justify-center gap-3 p-4 rounded-xl ring-1 transition-all",
            language === "en"
              ? "ring-2 ring-[#1F3A93] bg-[#1F3A93]/5"
              : "ring-black/10 bg-white hover:ring-black/20"
          ].join(" ")}
        >
          <span className="text-2xl">🇺🇸</span>
          <div className="text-left">
            <div className={[
              "font-medium",
              language === "en" ? "text-[#1F3A93]" : "text-gray-900"
            ].join(" ")}>{t("profile.english")}</div>
            <div className="text-xs text-gray-500">{t("profile.default")}</div>
          </div>
          {language === "en" && (
            <div className="ml-auto w-5 h-5 rounded-full bg-[#1F3A93] flex items-center justify-center">
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
        </button>

        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange("es")}
          className={[
            "flex items-center justify-center gap-3 p-4 rounded-xl ring-1 transition-all",
            language === "es"
              ? "ring-2 ring-[#1F3A93] bg-[#1F3A93]/5"
              : "ring-black/10 bg-white hover:ring-black/20"
          ].join(" ")}
        >
          <span className="text-2xl">🇪🇸</span>
          <div className="text-left">
            <div className={[
              "font-medium",
              language === "es" ? "text-[#1F3A93]" : "text-gray-900"
            ].join(" ")}>{t("profile.spanish")}</div>
            <div className="text-xs text-gray-500">{t("profile.spanish")}</div>
          </div>
          {language === "es" && (
            <div className="ml-auto w-5 h-5 rounded-full bg-[#1F3A93] flex items-center justify-center">
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
        </button>
      </div>

      <p className="text-xs text-gray-500 text-center">
        {t("profile.languageAppliedNote")}
      </p>
    </div>
  )
}

/* -----------------------------
 * Page
 * ---------------------------- */
export default function ProfilePage() {
  const { t, language, setLanguage } = useLanguage()
  const { user, logout, refreshSession } = useAuth()
  const qc = useQueryClient()
  const navigate = useNavigate()

  const [activeMembership, setActiveMembership] = React.useState<ResidentMembership | null>(
    () => getStoredActiveMembership()
  )
  const [allMemberships, setAllMemberships] = React.useState<ResidentMembership[]>(
    () => getStoredResidentMemberships()
  )
  const hasMultipleProperties = allMemberships.length > 1

  const profileForm = useForm<ProfileForm>({
    defaultValues: {
      fullName: "",
      email: "",
      phone: "",
      unit: "",
    },
  })


  const passwordForm = useForm<PasswordForm>({
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  })

  const [deleteOpen, setDeleteOpen] = React.useState(false)
  const [closingPassword, setClosingPassword] = React.useState("")
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const profile = useQuery({
    queryKey: ["resident-profile", user?.id, activeMembership?.resident_id],
    queryFn: getResidentProfile,
    refetchOnWindowFocus: false,
  })

  React.useEffect(() => {
    if (!profile.data) return
    const data = profile.data.data
    if (!profileForm.formState.isDirty) {
      profileForm.reset({ fullName: data.full_name, email: data.email, phone: data.phone, unit: data.unit.unit_code })
    }
    setActiveMembership(data.active_membership)
    setAllMemberships(data.memberships)
  }, [profile.data, profileForm])

  function failure(cause: unknown) {
    // Never log Axios errors here: request bodies include current/new passwords.
    const payload = (cause as { response?: { data?: Record<string, unknown> } })?.response?.data
    const messages = payload ? Object.values(payload).flat().filter(value => typeof value === "string") : []
    setError(messages.length ? messages.join(" ") : t("common.error"))
  }

  async function onSaveProfile(data: ProfileForm) {
    setError(null)
    try {
      const saved = await updateResidentProfile({ full_name: data.fullName, phone: data.phone, language })
      profileForm.reset({ fullName: saved.data.full_name, email: saved.data.email, phone: saved.data.phone, unit: saved.data.unit.unit_code })
      qc.setQueryData(["resident-profile", user?.id, activeMembership?.resident_id], saved)
      localStorage.setItem("resident_name", saved.data.full_name)
      await refreshSession()
      toast.success(t("profile.alert.profileUpdated"))
    } catch (cause) { failure(cause) }
  }

  async function onChangePassword(data: PasswordForm) {
    setError(null)
    if (data.newPassword !== data.confirmPassword) {
      setError(t("profile.alert.passwordMismatch"))
      return
    }
    try {
      await changePassword(data.currentPassword, data.newPassword)
      passwordForm.reset()
      await logout()
      navigate("/login", { replace: true })
      toast.success(t("profile.passwordSignInAgain"))
    } catch (cause) { failure(cause) }
  }

  async function onLanguageChange(value: "en" | "es") {
    setBusy(true)
    setError(null)
    try {
      const saved = await updateResidentProfile({ language: value })
      qc.setQueryData(["resident-profile", user?.id, activeMembership?.resident_id], saved)
      setLanguage(value, false)
    } catch (cause) { failure(cause) }
    finally { setBusy(false) }
  }

  async function onDeactivateAccount() {
    setBusy(true)
    setError(null)
    try {
      await deactivateResidentAccount(closingPassword)
      setClosingPassword("")
      await logout()
      navigate("/login", { replace: true })
    } catch (cause) { failure(cause) }
    finally { setBusy(false) }
  }

  const initials = profileForm.watch("fullName").split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]).join("") || "—"

  return (
    <div className="space-y-6 max-w-3xl mx-auto px-3 sm:px-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="rounded-2xl ring-1 ring-black/10 p-2.5 bg-white shadow-sm">
          <UserIcon className="w-5 h-5 text-gray-700" />
        </div>
        <div>
          <h2 className="text-[20px] font-semibold tracking-[-0.01em]">{t("profile.title")}</h2>
          <p className="text-gray-600 text-sm">
            {t("profile.personalInfoDesc")}
          </p>
        </div>
      </div>

      {error && !deleteOpen && <p role="alert" className="rounded-xl bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      {profile.isPending && <p role="status">{t("common.loading")}</p>}
      {profile.isError && <p role="alert">{t("profile.loadFailed")}</p>}

      {/* Profile — Apple-like card */}
      <Card variant="glass" className="rounded-2xl p-0 shadow-md bg-white/80 backdrop-blur-sm">
        <CardHeader className="px-4 sm:px-6 pt-6 pb-2">
          <CardTitle className={sectionTitle}>{t("profile.personalInfo")}</CardTitle>
          <CardDescription className={sectionDesc}>
            {t("profile.personalInfoViewOnlyDesc")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 px-4 sm:px-6 pb-5 sm:pb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5">
            <Avatar className="h-20 w-20 ring-1 ring-black/5 shadow-sm">
              <AvatarFallback className="text-lg">{initials}</AvatarFallback>
            </Avatar>

          </div>

          <form
            className="grid grid-cols-1 sm:grid-cols-2 gap-4"
            onSubmit={profileForm.handleSubmit(onSaveProfile)}
          >
            <div className="min-w-0">
              <Label htmlFor="fullName" className="text-[15px]">{t("profile.fullName")}</Label>
              <Input id="fullName" disabled={!profile.data} maxLength={200} className="w-full" {...profileForm.register("fullName", { required: true })} />
            </div>
            <div className="min-w-0">
              <Label htmlFor="email" className="text-[15px]">{t("profile.email")}</Label>
              <Input id="email" type="email" readOnly className="w-full" {...profileForm.register("email")} />
            </div>
            <div className="min-w-0">
              <Label htmlFor="phone" className="text-[15px]">{t("profile.phone")}</Label>
              <PhoneInputField 
                id="phone" 
                value={profileForm.watch("phone")} 
                onChange={(val) => profileForm.setValue("phone", val || "", { shouldDirty: true })} 
              />
            </div>
            <div className="min-w-0">
              <Label htmlFor="unit" className="text-[15px]">{t("profile.unit")}</Label>
              <Input id="unit" disabled className="w-full" {...profileForm.register("unit")} />
            </div>

            <div className="sm:col-span-2 flex flex-wrap items-center justify-end gap-2">
              <PrimaryButton type="submit" disabled={!profile.data || profileForm.formState.isSubmitting || busy}>{t("profile.saveProfile")}</PrimaryButton>
            </div>
          </form>
        </CardContent>
      </Card>

      <NotificationPreferences />

      {/* Language Settings */}
      <Card variant="glass" className="rounded-2xl p-0 shadow-sm bg-white">
        <CardHeader className="px-4 sm:px-6 pt-6 pb-2">
          <CardTitle className={sectionTitle + " flex items-center gap-2"}>
            <Globe className="h-4 w-4" />
            {t("profile.language")}
          </CardTitle>
          <CardDescription className={sectionDesc}>
            {t("profile.languageDesc")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 px-4 sm:px-6 pb-5 sm:pb-6">
          <LanguageSelector onChange={onLanguageChange} disabled={busy || !profile.data} />
        </CardContent>
      </Card>

      {/* Security — amber rail kept (orange-lighter) */}
      <Card
        variant="glass"
        rail
        railColor="border-amber-400"
        railWidth="border-l-4"
        className="rounded-2xl p-0 shadow-sm bg-white"
      >
        <CardHeader className="px-4 sm:px-6 pt-6 pb-2">
          <CardTitle className={sectionTitle + " flex items-center gap-2"}>
            <Shield className="w-4 h-4" /> {t("profile.security")}
          </CardTitle>
          <CardDescription className={sectionDesc}>{t("profile.securityDesc")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5 px-4 sm:px-6 pb-5 sm:pb-6">
          <form
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
            onSubmit={passwordForm.handleSubmit(onChangePassword)}
          >
            <div className="min-w-0">
              <Label htmlFor="currentPassword" className="text-[15px]">{t("profile.currentPassword")}</Label>
              <Input id="currentPassword" type="password" autoComplete="current-password" maxLength={128} required className="w-full" {...passwordForm.register("currentPassword")} />
            </div>
            <div className="min-w-0">
              <Label htmlFor="newPassword" className="text-[15px]">{t("profile.newPassword")}</Label>
              <Input id="newPassword" type="password" autoComplete="new-password" maxLength={128} minLength={8} required className="w-full" {...passwordForm.register("newPassword")} />
            </div>
            <div className="min-w-0">
              <Label htmlFor="confirmPassword" className="text-[15px]">{t("profile.confirmPassword")}</Label>
              <Input id="confirmPassword" type="password" autoComplete="new-password" maxLength={128} minLength={8} required className="w-full" {...passwordForm.register("confirmPassword")} />
            </div>

            <div className="lg:col-span-3 sm:col-span-2 col-span-1 flex flex-wrap items-center justify-end gap-2">
              <QuietButton type="button" onClick={async () => { await logout(); navigate("/login", { replace: true }) }}>
                <LogOut className="w-4 h-4" />
                {t("common.logout")}
              </QuietButton>
              <PrimaryButton type="submit" disabled={passwordForm.formState.isSubmitting || busy} className="gap-2">
                <KeyRound className="w-4 h-4" />
                {t("profile.changePassword")}
              </PrimaryButton>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Install App */}
      <Card variant="glass" className="rounded-2xl p-0 shadow-sm bg-white">
        <CardHeader className="px-4 sm:px-6 pt-6 pb-2">
          <CardTitle className={sectionTitle + " flex items-center gap-2"}>
            <ArrowDownToLine className="w-4 h-4" />
            {t("profile.installApp")}
          </CardTitle>
          <CardDescription className={sectionDesc}>
            {t("profile.appSettingsDesc")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 px-4 sm:px-6 pb-5 sm:pb-6">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
            <InstallPWAButton big />
            <QuietButton
              onClick={() => {
                const el = document.getElementById("install-help")
                el?.scrollIntoView({ behavior: "smooth", block: "start" })
              }}
            >
              <HelpCircle className="w-4 h-4" />
              {t("profile.installHelp")}
            </QuietButton>
          </div>

          <Callout icon={<MonitorSmartphone className="w-4 h-4" />}>
            {t("profile.install.calloutLine1")} {t("profile.install.calloutLine2")}
          </Callout>

          <div id="install-help" className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <div className="rounded-xl ring-1 ring-black/5 p-4 bg-white">
              <h4 className="font-medium text-[15px] mb-1">{t("profile.install.ios.title")}</h4>
              <ol className="list-decimal ml-5 space-y-1 text-sm text-gray-700">
                <li>{t("profile.install.ios.step1.pre")} <strong>{t("profile.install.term.share")}</strong> {t("profile.install.ios.step1.post")}</li>
                <li>{t("profile.install.ios.step2.pre")} <strong>{t("profile.install.term.addToHomeScreen")}</strong>.</li>
                <li>{t("profile.install.ios.step3.pre")} <strong>{t("profile.install.term.add")}</strong>.</li>
              </ol>
              <p className="text-xs text-gray-500 mt-2">{t("profile.install.ios.note")}</p>
            </div>

            <div className="rounded-xl ring-1 ring-black/5 p-4 bg-white">
              <h4 className="font-medium text-[15px] mb-1">{t("profile.install.android.title")}</h4>
              <ol className="list-decimal ml-5 space-y-1 text-sm text-gray-700">
                <li>{t("profile.install.android.step1.pre")} <strong>{t("profile.install.term.menuDots")}</strong> {t("profile.install.android.step1.post")}</li>
                <li>{t("profile.install.android.step2.pre")} <strong>{t("profile.install.term.installApp")}</strong> {t("profile.install.android.step2.middle")} <strong>{t("profile.install.term.addToHomeScreen")}</strong>.</li>
                <li>{t("profile.install.android.step3")}</li>
              </ol>
            </div>

            <div className="rounded-xl ring-1 ring-black/5 p-4 bg-white">
              <h4 className="font-medium text-[15px] mb-1">{t("profile.install.desktop.title")}</h4>
              <ol className="list-decimal ml-5 space-y-1 text-sm text-gray-700">
                <li>{t("profile.install.desktop.step1.pre")} <strong>{t("profile.install.term.install")}</strong> {t("profile.install.desktop.step1.post")}</li>
                <li>{t("profile.install.desktop.step2.pre")} <strong>{t("profile.install.term.installApp")}</strong>.</li>
              </ol>
              <p className="text-xs text-gray-500 mt-2">{t("profile.install.desktop.note")}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Properties */}
      <Card variant="glass" className="rounded-2xl p-0 shadow-sm bg-white">
        <CardHeader className="px-4 sm:px-6 pt-6 pb-2">
          <CardTitle className={sectionTitle + " flex items-center gap-2"}>
            <Building2 className="w-4 h-4" /> {t("profile.properties")}
          </CardTitle>
          <CardDescription className={sectionDesc}>
            {t("profile.propertiesDesc")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 px-4 sm:px-6 pb-5 sm:pb-6">
          {activeMembership ? (
            <div className="rounded-xl ring-1 ring-black/5 bg-gray-50 px-4 py-3.5 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[15px] font-medium text-gray-900 truncate">
                  {activeMembership.building?.name || t("profile.unknownBuilding")}
                </p>
                <p className="text-sm text-gray-500 mt-0.5">
                  {t("profile.unit")} {activeMembership.unit?.unit_code || "—"}
                  {activeMembership.building?.code ? ` · ${activeMembership.building.code}` : ""}
                </p>
                <p className="text-xs text-gray-400 mt-1 uppercase tracking-wide">
                  {activeMembership.role_display}
                  {activeMembership.is_primary ? ` · ${t("profile.primaryContact")}` : ""}
                </p>
              </div>
              <span className="shrink-0 inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium bg-green-100 text-green-700">
                {t("profile.active")}
              </span>
            </div>
          ) : (
            <p className="text-sm text-gray-500">{t("profile.noActiveProperty")}</p>
          )}

          {hasMultipleProperties && (
            <QuietButton
              type="button"
              className="w-full justify-center gap-2"
              onClick={() => navigate("/resident/select-property")}
            >
              <ArrowLeftRight className="w-4 h-4" />
              {t("profile.switchProperty")} ({allMemberships.length})
            </QuietButton>
          )}
        </CardContent>
      </Card>

      {/* Danger Zone — rail kept (rose) */}
      <Card
        variant="glass"
        rail
        railColor="border-rose-500"
        railWidth="border-l-4"
        className="rounded-2xl p-0 shadow-sm bg-white"
      >
        <CardHeader className="px-4 sm:px-6 pt-6 pb-2">
          <CardTitle className="flex items-center gap-2 text-red-700">
            <Trash2 className="w-4 h-4" /> {t("profile.deactivateAccount")}
          </CardTitle>
          <CardDescription className={sectionDesc}>
            {t("profile.deactivateDesc")}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center justify-end gap-2 px-4 sm:px-6 pb-5 sm:pb-6">
          <Button
            variant="destructive"
            onClick={() => { setError(null); setDeleteOpen(true) }}
            className="rounded-full h-10 px-5 bg-red-600 hover:bg-red-700 text-white disabled:opacity-100 disabled:bg-red-300"
          >
            {t("profile.confirmDeactivate")}
          </Button>
        </CardContent>
      </Card>

      {/* Delete confirmation */}
      <Dialog open={deleteOpen} onOpenChange={(isOpen) => { if (!busy && !isOpen) { setDeleteOpen(false); setClosingPassword("") } }}>
        <DialogOverlay className="fixed inset-0 bg-black/45 backdrop-blur-sm z-40" />
        <DialogContent className="z-50 sm:max-w-lg w-[calc(100%-1.5rem)] p-0 rounded-2xl border bg-white shadow-2xl">
          <DialogHeader className="px-4 sm:px-6 pt-6">
<DialogTitle>{t("profile.deactivateAccount")}?</DialogTitle>
            <DialogDescription>
              {t("profile.deactivateWarning")}
            </DialogDescription>
          </DialogHeader>
          <div className="px-4 sm:px-6 pb-6 pt-3 space-y-3 text-sm text-gray-600">
            <Label htmlFor="closing-password">{t("profile.currentPassword")}</Label>
            <Input id="closing-password" type="password" autoComplete="current-password" maxLength={128} value={closingPassword} onChange={event => setClosingPassword(event.target.value)} />
            {error && <p role="alert" className="text-red-700">{error}</p>}
          </div>
          <DialogFooter className="px-4 sm:px-6 pb-6">
            <QuietButton disabled={busy} onClick={() => { setDeleteOpen(false); setClosingPassword("") }}>{t("common.cancel")}</QuietButton>
            <Button disabled={busy || !closingPassword} onClick={onDeactivateAccount} className="rounded-full h-10 px-5 bg-red-600 hover:bg-red-700 text-white">
              {t("profile.confirmDeactivate")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

/* -----------------------------------
 * Install controls
 * ----------------------------------- */

function InstallPWAButton({ big = false }: { big?: boolean }) {
  const { t } = useLanguage()
  const { available, promptToInstall, installedOrUnsupported, displayMode } = useInstallPrompt()
  const label = available
    ? t("profile.install.button.installApp")
    : installedOrUnsupported
      ? t("profile.install.button.installedOrNotSupported")
      : t("profile.install.button.install")
  return (
    <PrimaryButton
      onClick={promptToInstall}
      disabled={!available}
      className={big ? "gap-2 h-11 px-5 text-[16px]" : "gap-2"}
      title={
        installedOrUnsupported
          ? displayMode === "standalone"
            ? t("profile.install.button.alreadyInstalled")
            : t("profile.install.button.notSupported")
          : t("profile.install.button.installApp")
      }
    >
      <Smartphone className="w-5 h-5" />
      {label}
    </PrimaryButton>
  )
}

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>
}

function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = React.useState<InstallPromptEvent | null>(null)
  const [available, setAvailable] = React.useState(false)
  const [displayMode, setDisplayMode] = React.useState<"browser" | "standalone" | "twa">("browser")

  React.useEffect(() => {
    const isStandalone =
      (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone === true
    setDisplayMode(isStandalone ? "standalone" : "browser")
  }, [])

  React.useEffect(() => {
    function onBeforeInstallPrompt(e: Event) {
      e.preventDefault()
      setDeferredPrompt(e as InstallPromptEvent)
      setAvailable(true)
    }
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt)
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt)
  }, [])

  React.useEffect(() => {
    const mq = window.matchMedia("(display-mode: standalone)")
    const onChange = () => setAvailable(!mq.matches && !!deferredPrompt)
    mq.addEventListener?.("change", onChange)
    return () => mq.removeEventListener?.("change", onChange)
  }, [deferredPrompt])

  async function promptToInstall() {
    if (!deferredPrompt) return
    deferredPrompt.prompt()
    const choice = await deferredPrompt.userChoice
    if (choice?.outcome === "accepted") {
      setAvailable(false)
    }
  }

  const installedOrUnsupported = displayMode === "standalone" || !available
  return { available, promptToInstall, installedOrUnsupported, displayMode }
}
